import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import ActionDropdownItem from "../../components/Common/PageTitle/ActionDropdownItem";
import MiniStatCard from "../../components/Banking/MiniStatCard";
import ActivityLoader from "../../components/Common/ActivityLoader";
import CallOut from "../../components/Common/CallOut";

import { formatDate, numberFormat } from "../../util/araknerdlib";
import { useCurrency } from "../../util/SystemVariables";
import ajaxLedger from "../../util/remote/ajaxLedger";
import { useAccounts } from "../../DataStore/AccountsContext";
import AccountUpdateModal from "../../components/Transaction/AccountUpdateModal";
import AlertNotification from "../../components/Common/AlertNotification";
import { useTheme } from "../../DataStore/ThemeContext";

const statusTone = (status) => {
  switch (status) {
    case "Posted":
    case "Approved":
      return "success";
    case "Pending":
      return "warning";
    case "Rejected":
    case "Failed":
      return "danger";
    default:
      return "success";
  }
};

const normalizeLine = (line) => ({
  ...line,
  account_number: line?.account?.account_number || "—",
  account_name: line?.account?.account_name || "Unknown Account",
  account_type: line?.account?.account_type || "",
  debit: Number(line?.debit) || 0,
  credit: Number(line?.credit) || 0,
});

const TransactionLedgerDetailPage = () => {
  const { ledgerGrouping } = useParams();
  const navigate = useNavigate();
  const currency = useCurrency();
  const { setNotify } = useTheme();

  const {
    accountsCatalogue,
    getAllAccounts,
    getPrimitiveAccountsByType,
  } = useAccounts();

  const [loading, setLoading] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [serverMessage, setServerMessage] = useState("");
  const [searchLedgerGrouping, setSearchLedgerGrouping] = useState(ledgerGrouping || "");

  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);

  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);

  useEffect(() => {
    setSearchLedgerGrouping(ledgerGrouping || "");
  }, [ledgerGrouping]);

  useEffect(() => {
    getAllAccounts();
  }, []);

  const fetchTransaction = useCallback(async (grouping) => {
    if (!grouping) {
      setTransaction(null);
      setNotFound(true);
      return;
    }

    setLoading(true);
    setNotFound(false);
    setServerMessage("");

    try {
      const response = await ajaxLedger.lookupLedgerEntry({
        ledger_grouping: grouping,
      });

      if (response?.status === "OK" && response?.details) {
        setTransaction(response.details);
        setNotFound(false);
      } else {
        setTransaction(null);
        setNotFound(true);
        setServerMessage(response?.message || "No ledger transaction was found.");
      }
    } catch (error) {
      console.error(error);
      setTransaction(null);
      setNotFound(true);
      setServerMessage("Failed to fetch ledger transaction. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransaction(ledgerGrouping);
  }, [ledgerGrouping, fetchTransaction]);

  const lines = useMemo(() => {
    return Array.isArray(transaction?.lines)
      ? transaction.lines.map(normalizeLine)
      : [];
  }, [transaction]);

  const meta = transaction?.meta || {};

  const totals = useMemo(() => {
    if (transaction?.totals) {
      return {
        debit: Number(transaction.totals.dr) || 0,
        credit: Number(transaction.totals.cr) || 0,
        balanced: !!transaction.totals.balanced,
      };
    }

    return lines.reduce(
      (t, line) => ({
        debit: t.debit + (Number(line.debit) || 0),
        credit: t.credit + (Number(line.credit) || 0),
        balanced: true,
      }),
      { debit: 0, credit: 0, balanced: true }
    );
  }, [transaction, lines]);

  const amount = Math.max(totals.debit, totals.credit);
  const isBalanced = transaction?.totals
    ? totals.balanced
    : Math.abs(totals.debit - totals.credit) < 0.0001;

  const selectableAccounts = useMemo(() => {
    if (!selectedLine?.account_type) return [];
    return getPrimitiveAccountsByType(selectedLine.account_type);
  }, [selectedLine, accountsCatalogue]);

  const selectedAccount = useMemo(() => {
    return selectableAccounts.find(
      (a) => String(a.account_id) === String(selectedAccountId)
    ) || null;
  }, [selectedAccountId, selectableAccounts]);

  const handleSearch = (e) => {
    e.preventDefault();

    const value = searchLedgerGrouping.trim();
    if (!value) return;

    navigate(`/transaction-ledger/${value}`);
  };

  const openAccountModal = async (line) => {
    await getAllAccounts();

    setSelectedLine(line);
    setSelectedAccountId(line.account_id || "");
    setShowAccountModal(true);
  };

  const closeAccountModal = () => {
    if (savingAccount) return;

    setSelectedLine(null);
    setSelectedAccountId("");
    setShowAccountModal(false);
  };

  const handleAccountUpdate = async (newAccount) => {
    if (!selectedLine || !selectedAccount) return;

    setSavingAccount(true);
    try {
     const response = await ajaxLedger.updateLedgerEntryAccount({
        ledger_id: selectedLine.id,
        new_account_id: newAccount?.account?.account_id,
      });
      if(response.status === "OK"){
        setNotify(<AlertNotification type="success" message={response.message || "Ledger entry account updated successfully."} iconClass="fa fa-check-circle"/>);
        } else {
            setNotify(<AlertNotification type="danger" message={response.message || "Failed to update ledger entry account."} iconClass="fa fa-times-circle"/>);
            return;
        }

      setTransaction((prev) => ({
        ...prev,
        lines: prev.lines.map((line) =>
          Number(line.id) === Number(selectedLine.id)
            ? {
                ...line,
                account_id: selectedAccount.account_id,
                account: {
                  ...(line.account || {}),
                  account_id: selectedAccount.account_id,
                  account_number: selectedAccount.account_number,
                  account_name: selectedAccount.account_name,
                  account_type: selectedAccount.account_type,
                },
              }
            : line
        ),
      }));

      closeAccountModal();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingAccount(false);
    }
  };

  const startDescriptionEdit = () => {
    setDescriptionDraft(meta.description || "");
    setEditingDescription(true);
  };

  const cancelDescriptionEdit = () => {
    setEditingDescription(false);
    setDescriptionDraft("");
  };

  const handleDescriptionSave = async () => {
    const value = descriptionDraft.trim();
    if (!value) return;

    setSavingDescription(true);

    try {
       const response = await ajaxLedger.updateDescription({
        group_id: meta.ledger_grouping,
        new_description: value,
      });
      if(response.status === "OK"){
        setNotify(<AlertNotification type="success" message={response.message || "Ledger entry description updated successfully."} iconClass="fa fa-check-circle"/>);
        } else {
            setNotify(<AlertNotification type="danger" message={response.message || "Failed to update ledger entry description."} iconClass="fa fa-times-circle"/>);
            return;
        }

      setTransaction((prev) => ({
        ...prev,
        meta: {
          ...prev.meta,
          description: value,
        },
        lines: prev.lines.map((line) => ({
          ...line,
          description: value,
        })),
      }));

      setEditingDescription(false);
    } catch (error) {
      //console.error(error);
    } finally {
      setSavingDescription(false);
    }
  };

  const PageActions = () => (
    <>
      <ActionDropdownItem
        iconClass="fa fa-sync"
        label="Refresh"
        onClick={() => fetchTransaction(ledgerGrouping)}
      />
      <ActionDropdownItem
        iconClass="fa fa-arrow-left"
        label="Back to Ledger"
        onClick={() => navigate("/transaction-ledger")}
      />
    </>
  );

  return (
    <>
      <PageContainer>
        <MainContent>
          <PageTitle
            title="Transaction Ledger Details"
            description="View the complete journal lines and posting details for this transaction."
            iconClass="fa fa-receipt"
            PageActions={PageActions}
          />

          <div className="card custom-card mb-4">
            <div className="card-body">
              <form onSubmit={handleSearch}>
                <div className="row align-items-end">
                  <div className="col-lg-8">
                    <label className="tx-12 text-muted mb-1">Search Ledger Grouping</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter ledger grouping e.g. 1"
                      value={searchLedgerGrouping}
                      onChange={(e) => setSearchLedgerGrouping(e.target.value)}
                    />
                  </div>

                  <div className="col-lg-4 mt-3 mt-lg-0">
                    <button type="submit" className="btn btn-primary btn-block">
                      <i className="fa fa-search mr-2" />
                      Search Transaction
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {loading && <ActivityLoader width={20} />}

          {!loading && notFound && (
            <CallOut
              type="info"
              title="Transaction Not Found"
              message={serverMessage || "No ledger transaction was found for the selected ledger grouping."}
            />
          )}

          {!loading && transaction && (
            <>
              <div className="row">
                <div className="col-md-3">
                  <MiniStatCard
                    icon="fa-hashtag"
                    label="Ledger Grouping"
                    value={meta.ledger_grouping || "—"}
                    tone="primary"
                    hint="Grouped journal entry identifier."
                    loading={false}
                  />
                </div>

                <div className="col-md-3">
                  <MiniStatCard
                    icon="fa-money-bill-wave"
                    label="Transaction Amount"
                    value={`${currency} ${numberFormat(amount)}`}
                    tone="success"
                    hint="Total transaction amount."
                    loading={false}
                  />
                </div>

                <div className="col-md-3">
                  <MiniStatCard
                    icon={isBalanced ? "fa-check-circle" : "fa-exclamation-triangle"}
                    label="Balance Status"
                    value={isBalanced ? "Balanced" : "Out of Balance"}
                    tone={isBalanced ? "success" : "danger"}
                    hint="Debit and credit totals should match."
                    loading={false}
                  />
                </div>

                <div className="col-md-3">
                  <MiniStatCard
                    icon="fa-calendar"
                    label="Transaction Date"
                    value={meta.transaction_date ? formatDate(meta.transaction_date) : "—"}
                    tone="warning"
                    hint="Date when the transaction occurred."
                    loading={false}
                  />
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-lg-8">
                  <div className="card custom-card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0">Journal Lines</h6>
                        <small className="text-muted">
                          Debit and credit breakdown. Click an account code to edit the transacting account.
                        </small>
                      </div>

                      <span className={`badge badge-${statusTone("Posted")}`}>POSTED</span>
                    </div>

                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-hover table-striped table-bordered mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: 110 }}>A/C Code</th>
                              <th>Account</th>
                              <th>Description</th>
                              <th className="text-right">Debit</th>
                              <th className="text-right">Credit</th>
                            </tr>
                          </thead>

                          <tbody>
                            {lines.map((line) => (
                              <tr key={line.id}>
                                <td className="text-monospace">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 font-weight-bold text-monospace ledger-account-link"
                                    onClick={() => openAccountModal(line)}
                                    title="Change transacting account"
                                  >
                                    {line.account_number}
                                  </button>
                                </td>
                                <td>
                                  <div>{line.account_name}</div>
                                  <small className="text-muted">{line.account_type}</small>
                                </td>
                                <td>{line.description}</td>
                                <td className="text-right">
                                  {line.debit > 0 ? numberFormat(line.debit) : ""}
                                </td>
                                <td className="text-right text-danger">
                                  {line.credit > 0 ? numberFormat(line.credit) : ""}
                                </td>
                              </tr>
                            ))}

                            <tr className="font-weight-bold">
                              <td colSpan="3">Total</td>
                              <td className="text-right">{numberFormat(totals.debit)}</td>
                              <td className="text-right text-danger">{numberFormat(totals.credit)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="card custom-card mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Transaction Summary</h6>

                      {!editingDescription && (
                        <button
                          type="button"
                          className="btn btn-sm btn-light"
                          onClick={startDescriptionEdit}
                        >
                          <i className="fa fa-edit mr-1" />
                          Edit
                        </button>
                      )}
                    </div>

                    <div className="card-body">
                      <DetailItem label="Reference" value={meta.transaction_ref} strong />
                      <DetailItem label="Attachment" value={meta.attachment} />
                      <DetailItem label="Opening Balance" value={meta.opening_balance ? "Yes" : "No"} />
                      <DetailItem label="Reconciled" value={meta.reconciled ? "Yes" : "No"} />

                      <div className="mb-3">
                        <small className="text-muted">Description</small>

                        {!editingDescription ? (
                          <div>{meta.description || "—"}</div>
                        ) : (
                          <>
                            <textarea
                              className="form-control mt-1"
                              rows={4}
                              value={descriptionDraft}
                              onChange={(e) => setDescriptionDraft(e.target.value)}
                              disabled={savingDescription}
                            />

                            <div className="d-flex justify-content-end mt-2" style={{ gap: 8 }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-light"
                                onClick={cancelDescriptionEdit}
                                disabled={savingDescription}
                              >
                                Cancel
                              </button>

                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={handleDescriptionSave}
                                disabled={savingDescription || !descriptionDraft.trim()}
                              >
                                {savingDescription ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin mr-1" />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-save mr-1" />
                                    Save
                                  </>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="card custom-card">
                    <div className="card-header">
                      <h6 className="mb-0">Audit Trail</h6>
                    </div>

                    <div className="card-body">
                      <AuditItem
                        title="Created"
                        user={meta.created_by?.full_name || meta.created_by?.username || "System"}
                        date={meta.created_at}
                        notes="Ledger transaction was created."
                      />

                      <AuditItem
                        title="Updated"
                        user={meta.updated_by?.full_name || meta.updated_by?.username || "—"}
                        date={meta.updated_at}
                        notes="Latest ledger transaction update."
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <Link to="/transaction-ledger" className="btn btn-light">
                  <i className="fa fa-arrow-left mr-2" />
                  Back to Ledger
                </Link>
              </div>
            </>
          )}
        </MainContent>
      </PageContainer>

      <AccountUpdateModal
        show={showAccountModal}
        line={selectedLine}
        saving={savingAccount}
        onClose={closeAccountModal}
        onSave={handleAccountUpdate}
        />
    </>
  );
};

const DetailItem = ({ label, value, strong = false }) => (
  <div className="mb-3">
    <small className="text-muted">{label}</small>
    <div className={strong ? "font-weight-bold" : ""}>{value || "—"}</div>
  </div>
);

const AuditItem = ({ title, user, date, notes }) => (
  <div className="mb-3 pb-3 border-bottom">
    <div className="font-weight-bold">
      <i className="fa fa-circle mr-2 text-primary" style={{ fontSize: 8 }} />
      {title}
    </div>
    <div className="text-muted small">{date ? formatDate(date) : "—"}</div>
    <div className="small mt-1">{notes}</div>
    <div className="small text-muted mt-1">By {user || "—"}</div>
  </div>
);



export default TransactionLedgerDetailPage;